/**
 * Delete cookies whose category the visitor refused.
 *
 * A CMP can't tell a third-party script "run but set no cookie" — so beyond
 * blocking the scripts up-front (auto-block.ts) we also remove the cookies a
 * refused category declared. This catches cookies set BEFORE the choice was
 * made, or by a script the owner didn't tag. It is a safety net, not a
 * replacement for prior blocking: a script left running will simply re-set its
 * cookie on its next execution.
 *
 * Scope:
 *   - Only categories the visitor set to `false` are purged.
 *   - "necessary" is never touched (always granted, legally exempt).
 *   - Only the names declared in settings.cookies[cat] are removed — we never
 *     blindly wipe the visitor's cookie jar. A declared name ending with `*`
 *     is a prefix (`_vk_*` → every cookie starting with `_vk_`): some vendors
 *     suffix their cookies per site or per session, so an exact name can
 *     never be declared for them.
 *   - Gated by compliance.purgeRefusedCookies (default on); no-op when the
 *     cookie inventory is empty.
 */

import type { BannerSettings, ConsentChoices } from "./types.js";

const EXPIRED = "Thu, 01 Jan 1970 00:00:00 GMT";

/** Categories that may carry deletable cookies (everything but "necessary"). */
const PURGEABLE: (keyof ConsentChoices)[] = ["analytics", "personalization", "marketing", "social"];

/**
 * Attempt to delete a cookie by name across the domain/path combinations a
 * cookie might have been set with, since we don't know its exact scope:
 *   - host-only + the document host + every parent domain (with leading dot)
 *   - path "/" and the current path
 */
function deleteCookie(name: string): void {
  const hostname = location.hostname;
  const domains: (string | undefined)[] = [undefined, hostname];
  const parts = hostname.split(".");
  // example.com, .example.com, sub.example.com … (skip the bare TLD)
  for (let i = 0; i < parts.length - 1; i++) {
    const suffix = parts.slice(i).join(".");
    domains.push(suffix, `.${suffix}`);
  }
  const paths = new Set(["/", location.pathname || "/"]);

  for (const path of paths) {
    for (const domain of domains) {
      let str = `${encodeURIComponent(name)}=; expires=${EXPIRED}; max-age=0; path=${path}`;
      if (domain) str += `; domain=${domain}`;
      document.cookie = str;
      // Some cookies were set with the raw (un-encoded) name — try both.
      if (encodeURIComponent(name) !== name) {
        let raw = `${name}=; expires=${EXPIRED}; max-age=0; path=${path}`;
        if (domain) raw += `; domain=${domain}`;
        document.cookie = raw;
      }
    }
  }
}

/** Names currently present in the visitor's cookie jar (decoded best-effort). */
function presentCookieNames(): string[] {
  const names: string[] = [];
  for (const part of document.cookie.split(";")) {
    const eq = part.indexOf("=");
    const raw = (eq >= 0 ? part.slice(0, eq) : part).trim();
    if (!raw) continue;
    names.push(raw);
    try {
      const decoded = decodeURIComponent(raw);
      if (decoded !== raw) names.push(decoded);
    } catch {
      // keep the raw name only
    }
  }
  return names;
}

/** Expand a declared name into the concrete cookies to delete (`*` = prefix). */
export function matchDeclaredName(declared: string, present: string[]): string[] {
  if (!declared.endsWith("*")) return [declared];
  const prefix = declared.slice(0, -1);
  if (!prefix) return []; // a bare "*" would wipe the jar — refuse
  return present.filter((n) => n.startsWith(prefix));
}

/**
 * Delete the declared cookies of every refused category. Safe to call on every
 * consent application (initial load with a stored choice, GPC auto-deny, and
 * each explicit submit).
 */
export function purgeRefusedCookies(settings: BannerSettings, choices: ConsentChoices): void {
  if (typeof document === "undefined") return;
  if (settings.compliance?.purgeRefusedCookies === false) return;
  const table = settings.cookies;
  if (!table) return;

  let present: string[] | null = null;
  for (const cat of PURGEABLE) {
    if (choices[cat] !== false) continue; // only refused categories
    const declared = table[cat];
    if (!declared?.length) continue;
    for (const ck of declared) {
      if (!ck.name) continue;
      if (ck.name.endsWith("*")) {
        present ??= presentCookieNames();
        for (const name of matchDeclaredName(ck.name, present)) deleteCookie(name);
      } else {
        deleteCookie(ck.name);
      }
    }
  }
}
